import * as FileRenameRulesCRUD from "app/crud/file-rename-rules.crud";
import { setErrorFlag, setLoadingFlag } from "app/store/modules/actions/loading-flag.actions";
import { getFileRenamePlaceholders } from "app/store/modules/selectors/file-rename-rules.selector";

export const actionTypes = {
  FILE_RENAME_RULES_LOADED: "[FILE_RENAME_RULES] FILE_RENAME_RULES_LOADED",
  FILE_RENAME_RULE_LOADED: "[FILE_RENAME_RULES] FILE_RENAME_RULE_LOADED",
  FILE_RENAME_RULE_CREATED: "[FILE_RENAME_RULES] FILE_RENAME_RULE_CREATED",
  FILE_RENAME_RULE_UPDATED: "[FILE_RENAME_RULES] FILE_RENAME_RULE_UPDATED",
  FILE_RENAME_RULE_REMOVED: "[FILE_RENAME_RULES] FILE_RENAME_RULE_REMOVED",
  FILE_RENAME_PLACEHOLDERS_LOADED: "[FILE_RENAME_RULES] FILE_RENAME_PLACEHOLDERS_LOADED",
  FILE_RENAME_DOCUMENT_TYPES_LOADED: "[FILE_RENAME_RULES] FILE_RENAME_DOCUMENT_TYPES_LOADED",
  FILE_RENAME_LENDERS_LOADED: "[FILE_RENAME_RULES] FILE_RENAME_LENDERS_LOADED",
  CHANGE_PAGE: "[FILE_RENAME_RULES] CHANGE_PAGE",
  UPDATE_FILTER: "[FILE_RENAME_RULES] UPDATE_FILTER",
};

export const flagNames = {
  FILE_RENAME_RULES: "[FILE_RENAME_RULES] FILE_RENAME_RULES",
  FILE_RENAME_RULE: "[FILE_RENAME_RULES] FILE_RENAME_RULE",
  CREATE: "[FILE_RENAME_RULES] CREATE",
  UPDATE: "[FILE_RENAME_RULES] UPDATE",
  BULK_UPDATE: "[FILE_RENAME_RULES] BULK_UPDATE",
  DELETE: "[FILE_RENAME_RULES] DELETE",
  PLACEHOLDERS: "[FILE_RENAME_RULES] PLACEHOLDERS",
  DOCUMENT_TYPES: "[FILE_RENAME_RULES] DOCUMENT_TYPES",
  LENDERS: "[FILE_RENAME_RULES] LENDERS",
};

// Fetch all file rename rules
export const loadFileRenameRules = () => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.FILE_RENAME_RULES, true));
    try {
      const { data } = await FileRenameRulesCRUD.getAllFileRenameRules();
      dispatch({
        type: actionTypes.FILE_RENAME_RULES_LOADED,
        payload: { list: data.data },
      });
      dispatch(setLoadingFlag(flagNames.FILE_RENAME_RULES, false));
      return Promise.resolve({ data: data.data });
    } catch (e) {
      dispatch(setErrorFlag(flagNames.FILE_RENAME_RULES, e.toString()));
      return Promise.reject(e);
    }
  };
};

// Fetch a single file rename rule by id
export const loadFileRenameRule = (id) => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.FILE_RENAME_RULE, true));
    try {
      const { data } = await FileRenameRulesCRUD.getFileRenameRuleById(id);
      dispatch({
        type: actionTypes.FILE_RENAME_RULE_LOADED,
        payload: { rule: data.data },
      });
      dispatch(setLoadingFlag(flagNames.FILE_RENAME_RULE, false));
      return Promise.resolve({ data });
    } catch (e) {
      dispatch(setErrorFlag(flagNames.FILE_RENAME_RULE, e.toString()));
      return Promise.reject(e);
    }
  };
};

// Create a new file rename rule
export const createFileRenameRule = (ruleData) => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.CREATE, true));
    try {
      const { data } = await FileRenameRulesCRUD.createFileRenameRule(ruleData);
      dispatch({
        type: actionTypes.FILE_RENAME_RULE_CREATED,
        payload: { rule: data },
      });

      // Refresh document types as they might have changed
      await dispatch(loadFileRenameDocumentTypes());

      dispatch(loadFileRenameRules());
      dispatch(setLoadingFlag(flagNames.CREATE, false));
      return Promise.resolve({ data });
    } catch (e) {
      dispatch(setErrorFlag(flagNames.CREATE, e.toString()));
      return Promise.reject(e);
    }
  };
};

// Update an existing file rename rule
export const updateFileRenameRule = (id, ruleData) => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.UPDATE, true));
    try {
      const { data } = await FileRenameRulesCRUD.updateFileRenameRule(id, ruleData);
      dispatch({
        type: actionTypes.FILE_RENAME_RULE_UPDATED,
        payload: { rule: data },
      });

      // Refresh document types as they might have changed
      await dispatch(loadFileRenameDocumentTypes());

      dispatch(loadFileRenameRules());
      dispatch(setLoadingFlag(flagNames.UPDATE, false));
      return Promise.resolve({ data });
    } catch (e) {
      dispatch(setErrorFlag(flagNames.UPDATE, e.toString()));
      return Promise.reject(e);
    }
  };
};

// Bulk update multiple file rename rules with settings
export const bulkUpdateFileRenameRules = (ruleIds, settings) => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.BULK_UPDATE, true));
    try {
      const { data } = await FileRenameRulesCRUD.bulkUpdateFileRenameRules(ruleIds, settings);

      // Refresh document types as they might have changed
      await dispatch(loadFileRenameDocumentTypes());

      dispatch(loadFileRenameRules());
      dispatch(setLoadingFlag(flagNames.BULK_UPDATE, false));
      return Promise.resolve({ data });
    } catch (e) {
      dispatch(setErrorFlag(flagNames.BULK_UPDATE, e.toString()));
      return Promise.reject(e);
    }
  };
};

// Remove a file rename rule
export const deleteFileRenameRule = (id) => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.DELETE, true));
    try {
      const { data } = await FileRenameRulesCRUD.removeFileRenameRule(id);
      dispatch({
        type: actionTypes.FILE_RENAME_RULE_REMOVED,
        payload: { id },
      });
      dispatch(loadFileRenameRules());
      dispatch(setLoadingFlag(flagNames.DELETE, false));
      return Promise.resolve({ data });
    } catch (e) {
      dispatch(setErrorFlag(flagNames.DELETE, e.toString()));
      return Promise.reject(e);
    }
  };
};

export const loadFileRenameDocumentTypes = (searchString = "") => {
  return async (dispatch) => {
    const { data } = await FileRenameRulesCRUD.getFileRenameDocumentTypes(searchString);

    dispatch({
      type: actionTypes.FILE_RENAME_DOCUMENT_TYPES_LOADED,
      payload: { documentTypes: data.data },
    });
  };
};

// Load file rename placeholders
export const loadFileRenamePlaceholders = () => {
  return async (dispatch, getStore) => {
    const store = getStore();
    const placeholders = getFileRenamePlaceholders(store);

    // If placeholders are already loaded, return them
    // instead of making a new request
    if (placeholders.length > 0) {
      return Promise.resolve({ data: placeholders });
    }

    dispatch(setLoadingFlag(flagNames.PLACEHOLDERS, true));

    try {
      const placeholders = await FileRenameRulesCRUD.getFileRenamePlaceholdersV2();

      await dispatch(loadFileRenameDocumentTypes());

      dispatch({
        type: actionTypes.FILE_RENAME_PLACEHOLDERS_LOADED,
        payload: { placeholders: placeholders.data.data },
      });

      dispatch(setLoadingFlag(flagNames.PLACEHOLDERS, false));
    } catch (e) {
      dispatch(setErrorFlag(flagNames.PLACEHOLDERS, e.toString()));
      return Promise.reject(e);
    }
  };
};

export const loadAllLenders = () => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.LENDERS, true));

    try {
      const { data } = await FileRenameRulesCRUD.getAllLenders();

      dispatch({
        type: actionTypes.FILE_RENAME_LENDERS_LOADED,
        payload: { lenders: data.data },
      });

      dispatch(setLoadingFlag(flagNames.LENDERS, false));
    } catch (e) {
      dispatch(setErrorFlag(flagNames.LENDERS, e.toString()));
      return Promise.reject(e);
    }
  };
};

export const setPage = (page) => {
  return {
    type: actionTypes.CHANGE_PAGE,
    payload: { page },
  };
};

export const updateFileRenameRulesFilter = (data) => {
  return {
    type: actionTypes.UPDATE_FILTER,
    payload: { data },
  };
};
